import * as XLSX from 'xlsx';
import { format } from 'date-fns';
import { Lead, ChannelPartner, Banker, DailyCommitment } from '@/types';

// Helper function to download workbook
const downloadWorkbook = (workbook: XLSX.WorkBook, filename: string) => {
  const excelBuffer = XLSX.write(workbook, { bookType: 'xlsx', type: 'array' });
  const data = new Blob([excelBuffer], { type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet' });
  const url = window.URL.createObjectURL(data);
  const link = document.createElement('a');
  link.href = url;
  link.download = filename;
  link.click();
  window.URL.revokeObjectURL(url);
};

// Export vendors/channel partners
export const exportVendors = (vendors: ChannelPartner[]) => {
  const data = vendors.map(vendor => ({
    'Name': vendor.name,
    'Email': vendor.email,
    'Phone': vendor.phone,
    'Company': vendor.companyName || '-',
    'Region': vendor.region || '-',
    'Product Type': vendor.productType || '-',
    'Status': vendor.isActive ? 'Active' : 'Inactive',
    'Commission Rate': vendor.commissionRate ? `${vendor.commissionRate}%` : '-',
    'Added On': vendor.createdAt ? format(new Date(vendor.createdAt), 'dd/MM/yyyy') : '-',
  }));

  const worksheet = XLSX.utils.json_to_sheet(data);
  const workbook = XLSX.utils.book_new();
  XLSX.utils.book_append_sheet(workbook, worksheet, 'Channel Partners');

  // Auto-size columns
  const maxWidth = 30;
  const wscols = Object.keys(data[0] || {}).map(() => ({ wch: maxWidth }));
  worksheet['!cols'] = wscols;

  downloadWorkbook(workbook, `Channel_Partners_${format(new Date(), 'dd-MM-yyyy')}.xlsx`);
};

// Export bankers
export const exportBankers = (bankers: Banker[]) => {
  const data = bankers.map(banker => ({
    'Name': banker.name,
    'Email': banker.email || '-',
    'Phone': banker.phone,
    'Bank': banker.bank || '-',
    'Branch': banker.branch || '-',
    'Designation': banker.designation || '-',
    'Product Expertise': banker.productExpertise || '-',
    'Status': banker.isActive ? 'Active' : 'Inactive',
    'Added On': banker.createdAt ? format(new Date(banker.createdAt), 'dd/MM/yyyy') : '-',
  }));

  const worksheet = XLSX.utils.json_to_sheet(data);
  const workbook = XLSX.utils.book_new();
  XLSX.utils.book_append_sheet(workbook, worksheet, 'Bankers');

  const maxWidth = 25;
  const wscols = Object.keys(data[0] || {}).map(() => ({ wch: maxWidth }));
  worksheet['!cols'] = wscols;

  downloadWorkbook(workbook, `Bankers_${format(new Date(), 'dd-MM-yyyy')}.xlsx`);
};

// Export products
export const exportProducts = (products: any[]) => {
  const data = products.map(product => ({
    'Product Name': product.name,
    'Bank': product.bank,
    'Category': product.category,
    'Min CIBIL': product.minCibil || '-',
    'Min Salary': product.minSalary || '-',
    'ROI Range': product.roiRange || '-',
    'Processing Fee': product.processingFee || '-',
    'Max Tenure': product.maxTenure || '-',
    'Product Details': product.productDetails || '-',
    'Sales Manager Notes': product.salesManagerNotes || '-',
    'Customer Notes': product.customerNotes || '-',
    'Created On': product.createdAt ? format(new Date(product.createdAt), 'dd/MM/yyyy') : '-',
  }));

  const worksheet = XLSX.utils.json_to_sheet(data);
  const workbook = XLSX.utils.book_new();
  XLSX.utils.book_append_sheet(workbook, worksheet, 'Products');

  const maxWidth = 30;
  const wscols = Object.keys(data[0] || {}).map(() => ({ wch: maxWidth }));
  worksheet['!cols'] = wscols;

  downloadWorkbook(workbook, `Products_${format(new Date(), 'dd-MM-yyyy')}.xlsx`);
};

// Export disbursements/leads
export const exportDisbursements = (leads: Lead[]) => {
  const data = leads.map(lead => ({
    'Customer Name': lead.name,
    'Phone': lead.phone,
    'Bank': lead.bank || '-',
    'Product': lead.loanType || '-',
    'Loan Amount': lead.loanAmount || '-',
    'Sanctioned Amount': lead.sanctionedAmount || '-',
    'Disbursed Amount': lead.disbursedAmount || '-',
    'Status': lead.status,
    'Stage': lead.stage,
    'Assigned To': lead.assignedTo || '-',
    'Created By': lead.createdBy || '-',
    'Application Date': lead.applicationDate ? format(new Date(lead.applicationDate), 'dd/MM/yyyy') : '-',
    'Sanction Date': lead.sanctionDate ? format(new Date(lead.sanctionDate), 'dd/MM/yyyy') : '-',
    'Disbursement Date': lead.disbursementDate ? format(new Date(lead.disbursementDate), 'dd/MM/yyyy') : '-',
    'Created On': lead.createdAt ? format(new Date(lead.createdAt), 'dd/MM/yyyy') : '-',
  }));

  const worksheet = XLSX.utils.json_to_sheet(data);
  const workbook = XLSX.utils.book_new();
  XLSX.utils.book_append_sheet(workbook, worksheet, 'Disbursements');

  const maxWidth = 20;
  const wscols = Object.keys(data[0] || {}).map(() => ({ wch: maxWidth }));
  worksheet['!cols'] = wscols;

  downloadWorkbook(workbook, `Disbursements_${format(new Date(), 'dd-MM-yyyy')}.xlsx`);
};

// Export attendance
export const exportAttendance = (attendanceData: any[]) => {
  const data = attendanceData.map(record => ({
    'Date': format(new Date(record.date), 'dd/MM/yyyy'),
    'Day': format(new Date(record.date), 'EEEE'),
    'User': record.userName || '-',
    'Phone': record.userPhone || '-',
    'Status': record.isPresent ? 'Present' : 'Absent',
    'Commitment': record.commitment || '-',
    'Tasks Completed': record.tasksCompleted || 0,
    'Login Time': record.loginTime || '-',
  }));

  const worksheet = XLSX.utils.json_to_sheet(data);
  const workbook = XLSX.utils.book_new();
  XLSX.utils.book_append_sheet(workbook, worksheet, 'Attendance');

  const maxWidth = 20;
  const wscols = Object.keys(data[0] || {}).map(() => ({ wch: maxWidth }));
  worksheet['!cols'] = wscols;

  downloadWorkbook(workbook, `Attendance_${format(new Date(), 'dd-MM-yyyy')}.xlsx`);
};

// Bulk import template generators
export const generateVendorTemplate = () => {
  const templateData = [
    {
      'Name': 'Example Vendor',
      'Email': 'vendor@example.com',
      'Phone': '9876543210',
      'Company': 'Example Company',
      'Region': 'North',
      'Product Type': 'Home Loan',
      'Commission Rate': '1.5',
    }
  ];

  const worksheet = XLSX.utils.json_to_sheet(templateData);
  const workbook = XLSX.utils.book_new();
  XLSX.utils.book_append_sheet(workbook, worksheet, 'Template');

  downloadWorkbook(workbook, 'Vendor_Import_Template.xlsx');
};

export const generateBankerTemplate = () => {
  const templateData = [
    {
      'Name': 'Example Banker',
      'Email': 'banker@example.com',
      'Phone': '9876543210',
      'Bank': 'SBI',
      'Branch': 'Main Branch',
      'Designation': 'Relationship Manager',
      'Product Expertise': 'Home Loan, Personal Loan',
    }
  ];

  const worksheet = XLSX.utils.json_to_sheet(templateData);
  const workbook = XLSX.utils.book_new();
  XLSX.utils.book_append_sheet(workbook, worksheet, 'Template');

  downloadWorkbook(workbook, 'Banker_Import_Template.xlsx');
};

export const generateProductTemplate = () => {
  const templateData = [
    {
      'Product Name': 'Home Loan Regular',
      'Bank': 'SBI',
      'Category': 'Home Loan',
      'Min CIBIL': '700',
      'Min Salary': '50000',
      'ROI Range': '8.5% - 9.5%',
      'Processing Fee': '0.5% + GST',
      'Max Tenure': '30 years',
      'Product Details': 'Flexible repayment options',
      'Sales Manager Notes': 'Good for salaried individuals',
      'Customer Notes': 'Low processing fee',
    }
  ];

  const worksheet = XLSX.utils.json_to_sheet(templateData);
  const workbook = XLSX.utils.book_new();
  XLSX.utils.book_append_sheet(workbook, worksheet, 'Template');

  downloadWorkbook(workbook, 'Product_Import_Template.xlsx');
};

// Parse imported Excel file
export const parseExcelFile = (file: File): Promise<any[]> => {
  return new Promise((resolve, reject) => {
    const reader = new FileReader();
    
    reader.onload = (e) => {
      try {
        const data = new Uint8Array(e.target?.result as ArrayBuffer);
        const workbook = XLSX.read(data, { type: 'array' });
        const firstSheet = workbook.Sheets[workbook.SheetNames[0]];
        const jsonData = XLSX.utils.sheet_to_json(firstSheet);
        resolve(jsonData);
      } catch (error) {
        reject(new Error('Failed to parse Excel file'));
      }
    };

    reader.onerror = () => reject(new Error('Failed to read file'));
    reader.readAsArrayBuffer(file);
  });
};
